import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Monitor, Heart, Users, Shield, Globe } from 'lucide-react';
import CTA from '../../components/CTA';

const BallycastleHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Ballycastle | Seasonal Stress & Fishing Community Support</title>
        <meta
          name="description"
          content="Professional hypnotherapy for Ballycastle's coastal community. Support for seasonal tourism anxiety, fishing industry stress, ferry worker pressure, and rural isolation. CNHC registered. Belfast clinic and online sessions available."
        />
        <meta name="keywords" content="hypnotherapy Ballycastle, hypnotherapist Ballycastle, seasonal anxiety, fishing industry stress, rural coastal mental health, Ould Lammas Fair stress, ferry worker anxiety, agricultural stress North Coast, therapist Ballycastle" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/ballycastle-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Ballycastle | Seasonal Stress & Fishing Community Support" />
        <meta property="og:description" content="Professional hypnotherapy for Ballycastle. Support for seasonal tourism stress, fishing industry pressures, rural isolation. Belfast clinic and online sessions available." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Serving Ballycastle",
            "description": "CNHC registered hypnotherapy serving Ballycastle and the North Coast. Specializing in seasonal employment anxiety, fishing industry mental health, rural coastal isolation, transport worker stress (Rathlin Island ferry operations), agricultural sector support, and Ould Lammas Fair pressure. In-person sessions available at our Belfast clinic or online sessions for flexible scheduling.",
            "url": "https://www.sharonclare-hypnotherapy.com/ballycastle-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "areaServed": {
              "@type": "City",
              "name": "Ballycastle, County Antrim",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "The Arzen Collective",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "The Arzen Collective, 13B Antrim Street",
                  "addressLocality": "Lisburn",
                  "addressRegion": "County Antrim",
                  "postalCode": "BT28",
                  "addressCountry": "GB"
                }
              },
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "addressRegion": "County Down",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Seasonal Employment Anxiety Support",
              "Fishing Industry Mental Health",
              "Rural Coastal Isolation Support",
              "Transport Worker Stress Management",
              "Agricultural Sector Mental Health"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Professional Hypnotherapy for Ballycastle
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Seasonal employment stress, fishing industry support, and rural coastal community mental health
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <Monitor className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700 font-semibold">
                  Online sessions from home, or in person at the Belfast and Lisburn clinics
                </p>
              </div>
              <p className="text-gray-600">
                Secure video sessions are the simplest option from Ballycastle. In-person appointments are available at Belfast Wellness Clinic on the Ormeau Road and at The Arzen Collective in Lisburn, with flexible scheduling that fits around shift work, ferry schedules, and seasonal demands.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy for Ballycastle's Coastal Working Community
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Ballycastle sits at the northern tip of County Antrim where traditional coastal life meets modern tourism pressures. As the gateway to Rathlin Island and home to the historic Ould Lammas Fair, this working coastal town experiences unique mental health challenges: seasonal income anxiety for tourism businesses, economic pressures on the remaining fishing fleet, transport worker stress managing ferry schedules to an island community, and the financial uncertainty that comes with relying on a few intense summer months to sustain year-round living. As a CNHC registered hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I understand the particular stresses of coastal working communities where traditional industries face decline while tourism brings seasonal boom-and-bust cycles.
              </p>
              <p>
                For Ballycastle residents, <strong>online sessions via secure video call</strong> are usually the most practical way to work together — no travel, and appointments that fit around unpredictable shift patterns, ferry schedules, and weather-dependent work. Whether you're managing financial anxiety during quiet winter months after tourist season ends, coping with the intense pressure of Ould Lammas Fair week when tens of thousands descend on your small town, dealing with the dangerous and economically precarious reality of fishing industry work, or navigating the responsibility of operating the lifeline ferry service to Rathlin Island, professional support is available without leaving home. If you would rather meet face to face, I hold clinics at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, where days and times vary and early evening appointments are available, and at The Arzen Collective, 13B Antrim Street, Lisburn.
              </p>
              <p>
                Ballycastle's tight-knit coastal community is one of its greatest strengths, but traditional attitudes and "everyone knows everyone" dynamics can make seeking mental health support feel visible. Connecting online, or traveling to the Belfast clinic on the Ormeau Road or the Lisburn clinic on Antrim Street, provides a degree of discretion while still accessing local, culturally aware care. I work with individuals facing seasonal employment stress, fishing industry pressures, agricultural sector mental health challenges from the surrounding hinterland, transport worker burnout, rural isolation, small town visibility concerns, and the economic vulnerability that comes with depending on tourism and declining traditional industries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Can Help Ballycastle's Community
            </h2>
            <div className="grid gap-8 sm:grid-cols-2">
              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Seasonal Employment Anxiety</h3>
                    <p className="text-gray-600">
                      Support for tourism sector workers facing financial stress during quiet winter months after intense summer season. Address anxiety about making three months of income last twelve months, uncertainty about next season's bookings, and the pressure to maximize earnings during short peak periods.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Fishing Industry Mental Health</h3>
                    <p className="text-gray-600">
                      Specialized support for fishermen managing dangerous working conditions, quota pressures, economic decline of traditional industry, weather dependency, and the isolation of working alone at sea. Address the unique mental health challenges of an industry under constant pressure.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Transport Worker Stress</h3>
                    <p className="text-gray-600">
                      Help for Rathlin Island ferry workers managing responsibility for island's lifeline service, weather-related schedule disruptions, passenger safety pressures, and the stress of operating critical transport infrastructure that an entire community depends upon daily.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Ould Lammas Fair Event Pressure</h3>
                    <p className="text-gray-600">
                      Support for local businesses and workers managing the intense annual pressure when one of Ireland's oldest traditional fairs brings tens of thousands of visitors to your small town. Address event anxiety, crowd management stress, and the exhaustion of one massive event that can make or break annual revenue.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Agricultural Sector Support</h3>
                    <p className="text-gray-600">
                      Help for farming community from surrounding rural hinterland facing isolation, economic pressures, weather dependency, and the unique mental health challenges of agricultural work. Address farming-specific stressors with culturally aware support through Rural Support network connections.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Rural Coastal Isolation</h3>
                    <p className="text-gray-600">
                      Support for those experiencing the unique isolation of coastal town life on the geographic edge - beautiful location but limited amenities, long distances to services, and the quiet loneliness of winter months when tourists disappear and the town empties out.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Small Town Privacy Concerns</h3>
                    <p className="text-gray-600">
                      Confidential support recognizing that tight-knit communities can make seeking mental health help feel visible. Traveling to our Belfast clinic or connecting online provides discretion while accessing culturally aware care from someone who understands coastal town dynamics.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Economic Vulnerability Anxiety</h3>
                    <p className="text-gray-600">
                      Help managing the financial stress that comes with economic dependence on seasonal tourism and declining traditional industries. Address worry about future viability of coastal town economy and personal financial security in uncertain times.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Why Ballycastle Residents Choose Sharon Clare
            </h2>
            <div className="grid gap-8 sm:grid-cols-2">
              <div className="flex items-start space-x-4">
                <Shield className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Professional Healthcare Background</h3>
                  <p className="text-gray-600">
                    15 years as a registered nurse at Belfast City Hospital before specializing in hypnotherapy. I understand healthcare systems, rural health challenges, and the particular barriers coastal communities face accessing mental health support.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Users className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Understanding of Seasonal Work Patterns</h3>
                  <p className="text-gray-600">
                    Experience supporting tourism sector workers, fishing communities, and agricultural populations facing the unique mental health impacts of seasonal income, weather-dependent work, and economic cycles beyond individual control.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Monitor className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Flexible Session Options</h3>
                  <p className="text-gray-600">
                    Choose in-person sessions at our Belfast clinic for face-to-face support, or online sessions via secure video call for convenience around shift work, ferry schedules, fishing trips, and seasonal business demands.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Heart className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Coastal Community Awareness</h3>
                  <p className="text-gray-600">
                    Understanding of the particular pressures of coastal working towns - seasonal economy stress, traditional industry decline, rural isolation, small town visibility dynamics, and the cultural context of North Coast communities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Common Questions from Ballycastle Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with seasonal income anxiety when I make most of my money in 3-4 summer months?
                </h3>
                <p className="text-gray-600">
                  Yes. Many Ballycastle tourism workers experience significant anxiety about seasonal income patterns - the pressure to maximize earnings during short peak periods, worry about making summer income last through winter, and stress about uncertain bookings for next season. Hypnotherapy helps retrain your nervous system's response to financial uncertainty, develop resilience during quiet periods, and reduce the constant worry that accompanies boom-and-bust seasonal work. We work on practical anxiety management for off-season months and building confidence in your ability to manage cyclical income patterns.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  I work in the fishing industry - can you help with the mental health side of dangerous, economically uncertain work?
                </h3>
                <p className="text-gray-600">
                  Absolutely. Fishing is one of the most psychologically demanding professions - dangerous working conditions, isolation at sea, weather dependency, quota pressures, economic decline of the industry, and constant uncertainty. Hypnotherapy addresses the cumulative stress of dangerous work, helps process traumatic incidents at sea, manages anxiety about economic viability of fishing as a livelihood, and provides support for the unique mental health challenges of working alone in hazardous conditions. I work with the fishing community understanding the cultural context and specific pressures of this traditional North Coast industry.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  How can hypnotherapy help ferry workers managing responsibility for island transport?
                </h3>
                <p className="text-gray-600">
                  Ferry workers to Rathlin Island carry unique responsibility - you're operating the lifeline service an entire community depends upon. Hypnotherapy helps manage the stress of passenger safety responsibility, anxiety around weather-related disruptions that strand islanders or visitors, pressure of schedule reliability, and the weight of knowing your work is critical infrastructure. We address performance anxiety, decision-making stress in difficult weather conditions, and the cumulative burden of responsibility that comes with operating essential transport services.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  I'm overwhelmed by the Ould Lammas Fair every August - can therapy help with that annual pressure?
                </h3>
                <p className="text-gray-600">
                  Yes. The Ould Lammas Fair is one of Ireland's oldest and largest traditional fairs - tens of thousands of visitors descending on Ballycastle for a few intense days creates enormous pressure for local businesses, hospitality workers, emergency services, and the entire town. Hypnotherapy helps manage anticipatory anxiety in the weeks before the event, performance stress during the fair itself, crowd management anxiety, and the exhaustion afterward. We can work on building resilience for this massive annual event that many Ballycastle livelihoods depend upon while also being physically and mentally overwhelming.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Will seeking help in a small town be kept completely confidential?
                </h3>
                <p className="text-gray-600">
                  Absolutely. I understand that tight-knit communities like Ballycastle can make seeking mental health support feel visible - "everyone knows everyone" dynamics are real. That's one reason traveling to our Belfast clinic or connecting online provides important discretion. Everything discussed in sessions is completely confidential under professional standards. Many clients appreciate that they can access culturally aware, local care (I understand North Coast communities) while maintaining privacy that may feel limited in a small coastal town.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  I'm from the farming community around Ballycastle - do you work with agricultural stress?
                </h3>
                <p className="text-gray-600">
                  Yes. I work with farming families from the rural hinterland around Ballycastle facing isolation, economic pressures, weather dependency, long working hours, and the unique mental health challenges of agricultural life. Hypnotherapy addresses farming-specific stressors while respecting the cultural context of rural agricultural communities. I can also connect you with Rural Support, a specialist charity providing mental health and practical support specifically for the farming community across Northern Ireland.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Can online sessions work if I have unpredictable shift patterns or weather-dependent work?
                </h3>
                <p className="text-gray-600">
                  Definitely. Online sessions via secure video call are ideal for those with irregular schedules - fishing trips that depend on weather and tides, ferry shifts that change, tourism work with split shifts, or agricultural work that follows seasonal demands rather than office hours. We can schedule sessions with flexibility, reschedule around weather disruptions or work demands, and you can connect from home without 30-40 minute drive time. Online sessions are fully professional and just as effective as in-person for hypnotherapy work.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  How do you address the rural isolation of living on the edge - beautiful but lonely?
                </h3>
                <p className="text-gray-600">
                  Rural coastal isolation is a unique experience - dramatic scenery and strong community but limited amenities, long distances to services, and profound loneliness especially in winter months when tourist season ends and the town empties. Hypnotherapy helps process the conflicting feelings of loving where you live while struggling with isolation, addresses seasonal affective patterns when winter brings darkness and emptiness, and builds resilience for the particular mental health challenges of living on the geographic edge. We work on connecting with the positive aspects of coastal life while managing the isolation that comes with it.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  I'm worried about the economic future of Ballycastle - fishing declining, tourism uncertain - can therapy help?
                </h3>
                <p className="text-gray-600">
                  Yes. Many coastal working towns face economic anxiety - traditional industries like fishing in decline, tourism vulnerable to external shocks (COVID demonstrated this), and uncertainty about long-term viability. Hypnotherapy doesn't change economic realities, but it helps manage the personal anxiety about an uncertain future, builds resilience for navigating economic change, and addresses the worry that can become consuming when your livelihood depends on vulnerable industries. We work on what you can control while accepting what you cannot, and developing mental health strategies for living with economic uncertainty.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  What are your fees and how quickly can I be seen?
                </h3>
                <p className="text-gray-600">
                  The initial consultation runs 90 minutes and includes assessment and your first session. After that, single sessions are 60 minutes, and a discounted 4-session package is available. Most clients see meaningful improvement within 4-6 sessions for specific issues like seasonal anxiety or event stress. I typically have availability within 1-2 weeks. Book directly via the booking page for fastest scheduling, or call 07932 139245 to discuss your needs and find a time that works around your work schedule.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              What North Coast Clients Say
            </h2>
            <div className="grid gap-8 sm:grid-cols-2">
              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="flex text-accent-600">
                    {'★'.repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Working in tourism on the North Coast means you're constantly anxious about money - will this summer be good enough to last the winter? Sharon helped me manage that financial stress cycle. I still have quiet months, but I don't spiral into panic about it anymore. The online sessions worked perfectly around my unpredictable café schedule during busy season."
                </p>
                <p className="text-gray-600 font-semibold">– Tourism sector worker, North Coast</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="flex text-accent-600">
                    {'★'.repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Fishing's in my family for generations, but the mental toll of dangerous work and watching the industry decline was crushing me. Couldn't talk to other fishermen about it - not how we operate. Sharon understood the pressure without me having to explain the culture. First time I've actually addressed what this work does to your head."
                </p>
                <p className="text-gray-600 font-semibold">– Fisherman, County Antrim</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="flex text-accent-600">
                    {'★'.repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Living on the coast is beautiful but isolating, especially winter when it's dark by 4pm and there's nothing happening. I was struggling with loneliness but felt guilty because 'look where I live!' Sharon helped me accept you can love a place and still find it hard. Seasonal affective disorder is real when you're on the edge."
                </p>
                <p className="text-gray-600 font-semibold">– Resident, North Coast</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="flex text-accent-600">
                    {'★'.repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "The Ould Lammas Fair is massive for our town but absolutely overwhelming for local businesses. Tens of thousands of people, two intense days, and if you mess it up that's your year's profit gone. Sharon worked with me before the fair on managing that pressure. I actually enjoyed it this year instead of just surviving it."
                </p>
                <p className="text-gray-600 font-semibold">– Business owner, North Coast</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="flex text-accent-600">
                    {'★'.repeat(5)}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Small town means everyone knows your business, so I appreciated being able to travel to the Belfast clinic for sessions. Sharon gets the North Coast - the seasonal stress, the tight-knit community dynamics, the economic anxiety about tourism and fishing both struggling. She didn't need me to explain why it's complicated."
                </p>
                <p className="text-gray-600 font-semibold">– Client, County Antrim</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Resources */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Crisis Support Resources
            </h2>
            <div className="bg-white p-8 rounded-xl">
              <p className="text-gray-700 mb-6">
                If you're experiencing a mental health crisis and need immediate support, these services are available 24/7:
              </p>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Lifeline NI</h3>
                  <p className="text-gray-600 mb-2">24/7 crisis helpline for anyone in distress or despair</p>
                  <a href="tel:08088088000" className="text-accent-600 hover:text-accent-700 font-semibold">
                    0808 808 8000
                  </a>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Northern Health and Social Care Trust Crisis Line</h3>
                  <p className="text-gray-600 mb-2">Mental health crisis support for Northern Trust area (includes Ballycastle)</p>
                  <a href="tel:08001833333" className="text-accent-600 hover:text-accent-700 font-semibold">
                    0800 183 3333
                  </a>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Rural Support</h3>
                  <p className="text-gray-600 mb-2">Specialist support for farming and rural communities facing emotional, financial, or practical challenges</p>
                  <a href="tel:08000 138 708" className="text-accent-600 hover:text-accent-700 font-semibold">
                    0800 138 708
                  </a>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Samaritans</h3>
                  <p className="text-gray-600 mb-2">Confidential emotional support available 24/7</p>
                  <a href="tel:116123" className="text-accent-600 hover:text-accent-700 font-semibold">
                    116 123
                  </a>
                </div>
              </div>
              <p className="text-gray-600 mt-6 text-sm">
                In a life-threatening emergency, always call 999 or go to your nearest A&E.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default BallycastleHypnotherapy;
